import { divide } from 'tio-ui/math';
import { on } from '@ember/modifier';
import Input from 'tio-reimagined/components/input';
import { getTranslationKeyForReportedSalaryForMatchPlan } from 'tio-common/utils/syf';
import Footer from 'tio-reimagined/components/footer';
import Modal from 'tio-reimagined/components/modal';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import * as v from 'valibot';
import Component from '@glimmer/component';
import { dropTask } from 'ember-concurrency';
import type MatchParticipantModel from 'tio-data/models/match-participant';
import type MatchPlanModel from 'tio-data/models/match-plan';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';

const string = String;

type ParticipantFormData = {
  reportedSalary?: string | number;
  reportedMonthlyPayment?: string | number;
};

interface SyfDashboardInsightsParticipantFormDialogSignature {
  Args: {
    matchParticipant: MatchParticipantModel;
    matchPlan: MatchPlanModel;
    didSubmit?: (savedModel: MatchParticipantModel) => void;
    onCancel?: () => void;
  };
  Element: HTMLDivElement;
}

class SyfDashboardInsightsParticipantFormDialog extends Component<SyfDashboardInsightsParticipantFormDialogSignature> {
  @service declare store: StoreService;

  @tracked errors: Record<string, string[]> = {};

  FormSchema = v.object({
    reportedSalary: v.pipe(
      v.string(),
      v.transform((input) => {
        // Only accept valid numeric formats: optional leading minus, digits, optional single decimal point
        const match = input.match(/^-?\d*(\.\d*)?$/);
        const dollars = match ? Number(input) : 0;
        return Math.round(dollars * 100); // Convert to cents
      }),
      v.number(),
      v.minValue(0, 'Reported salary must be at least $0')
    ),
    reportedMonthlyPayment: v.pipe(
      v.string(),
      v.transform((input) => {
        // Accept only valid numbers: optional leading minus, digits, optional single decimal point and digits
        const match = input.match(/^\s*-?\d*(\.\d{0,2})?\s*$/);
        if (!match || input.trim() === '' || input === '-' || input === '.' || input === '-.') {
          return NaN;
        }
        const dollars = Number(input.trim());
        return Math.round(dollars * 100); // Convert to cents
      }),
      v.number(),
      v.minValue(0, 'Reported monthly payment must be at least $0')
    ),
  });

  onSubmit = dropTask(async () => {
    const saved = await this.args.matchParticipant.save();
    this.args.didSubmit?.(saved);
  });

  onCancel = () => {
    this.args.matchParticipant.rollbackAttributes();
    this.args.onCancel?.();
  };

  handleFormChange = (_event: Event) => {
    const form = _event.currentTarget as HTMLFormElement;
    const fd = new FormData(form);
    const data = {
      reportedSalary: (fd.get('reportedSalary') as string) ?? '',
      reportedMonthlyPayment: (fd.get('reportedMonthlyPayment') as string) ?? '',
    };
    this.validateField(data);
  };

  handleFormSubmit = (_event: SubmitEvent) => {
    _event.preventDefault();
    this.onSubmit.perform();
  };

  validateField = (data: ParticipantFormData) => {
    const result = v.safeParse(this.FormSchema, data);

    if (result.success) {
      this.args.matchParticipant.setProperties(result.output);
      this.errors = {};
    } else {
      const validationErrors: Record<string, string[]> = {};

      for (const issue of result.issues) {
        const path = issue.path?.[0]?.key as string;
        if (path) {
          if (!validationErrors[path]) {
            validationErrors[path] = [];
          }
          validationErrors[path].push(issue.message);
        }
      }
      this.errors = validationErrors;
    }
  };

  <template>
    <Modal @isOpen={{true}} @onClose={{this.onCancel}} @size="xl" data-test-edit-dependent-dialog>
      <:body>
        <form {{on "change" this.handleFormChange}} {{on "submit" this.handleFormSubmit}}>
          <Input
            name="reportedMonthlyPayment"
            @label={{t "syf.dashboard.insights.estimated_monthly_payment"}}
            @value={{string (divide @matchParticipant.reportedMonthlyPayment 100)}}
            @errors={{this.errors.reportedMonthlyPayment}}
          >
            <:startContent>
              <span class="text-default-500">{{t "dollar"}}</span>
            </:startContent>
          </Input>
          <Input
            name="reportedSalary"
            @label={{t (getTranslationKeyForReportedSalaryForMatchPlan @matchPlan)}}
            @value={{string (divide @matchParticipant.reportedSalary 100)}}
            @errors={{this.errors.reportedSalary}}
          >
            <:startContent>
              <span class="text-default-500">{{t "dollar"}}</span>
            </:startContent>
          </Input>
        </form>
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.onSubmit.isRunning}}
          @onSubmit={{this.onSubmit.perform}}
          @onCancel={{this.onCancel}}
        />
      </:footer>
    </Modal>
  </template>
}

export default SyfDashboardInsightsParticipantFormDialog;
